/**
 * Live Trading Funds View & Exit Order Helpers.
 *
 * Formats broker account funds summary tables and calculates position exit quantities & protection prices.
 */

export interface FundsPayload {
  prfname?: string;
  actid?: string;
  cash?: number;
  daycash?: number;
  payin?: number;
  marginused?: number;
  brkcollamt?: number;
}

export interface FundsRowModel {
  label: string;
  value: string;
}

export interface FundsViewModel {
  sessionText: string;
  rows: FundsRowModel[];
}

/**
 * Builds view model data for the funds panel table.
 */
export function buildFundsViewModel(
  funds: FundsPayload | null | undefined,
  inrFormatter: (n: number | null | undefined) => string,
): FundsViewModel {
  const f = funds || {};
  const fmt =
    typeof inrFormatter === "function"
      ? inrFormatter
      : (n: any) => String(n ?? "0.00");
  const sessionText = `account: ${f.prfname || "—"}${f.actid ? ` (${f.actid})` : ""}`;
  const rows: FundsRowModel[] = [
    { label: "Cash", value: fmt(f.cash) },
    { label: "Day Cash", value: fmt(f.daycash) },
    { label: "Payin", value: fmt(f.payin) },
    { label: "Margin Used", value: fmt(f.marginused) },
    { label: "Collateral", value: fmt(f.brkcollamt) },
  ];
  return { sessionText, rows };
}

/**
 * Calculates exit quantity aligned to lot size, preventing partial closes smaller than 1 full lot.
 */
export function calculateCloseQuantity(
  netQty: number,
  fraction: number = 1,
  lotSize: number = 65,
): { qty: number; error?: string } {
  const rawQty = Math.abs(netQty);
  if (fraction >= 1) {
    return { qty: rawQty };
  }
  const lot = lotSize > 0 ? lotSize : 65;
  const lots = Math.floor((rawQty * fraction) / lot);
  if (lots < 1) {
    return {
      qty: 0,
      error: `Position too small for a ${fraction * 100}% close (rounds below 1 lot)`,
    };
  }
  return { qty: lots * lot };
}

/**
 * Calculates protective limit price for market-style exits with slippage protection bands.
 */
export function calculateProtectedLimitPrice(
  side: "BUY" | "SELL",
  ltp: number,
  protectionBand: number = 0.05,
): number {
  const b = protectionBand || 0;
  return side === "BUY" ? ltp * (1 + b) : ltp * (1 - b);
}
